import { Button, Input, Space, Tooltip } from 'antd';
import React from 'react';
import s from './s.module.less';
import CommonTable from 'components/CommonTable';
import { ProviderPageInfo } from 'types/kiwiHealth';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ColumnsType, TableProps } from 'antd/es/table';
// import useGetPrescriptionList from 'hooks/textVisit/useGetPrescriptionList';
import useGetGroupOrSelfPrescriptionList from 'hooks/group/useGetGroupOrSelfPrescriptionList';
import { EPrescriptionListSortBy, EPrescriptionListStatus, TPrescriptionListItem } from 'types/textVisit';
import dayjs from 'utils/dayjs';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import EditPrescribeMedicationModal from '../EditPrescribeMedicationModal';
import { EViewType, TPracticePrescriptionItem } from 'types/group';

const PAGE_SIZE = 10;

type PrescriptionsPatientTableProps = {
    showProviderColumn?: boolean;
    providerId?: number;
    viewType?: EViewType;
};

const PrescriptionsPatientTable = ({ showProviderColumn, providerId, viewType }: PrescriptionsPatientTableProps) => {
    // const [data, total, loading, fetchData] = useGetPrescriptionList({
    //     current: 1,
    //     size: PAGE_SIZE,
    // });
    const [data, total, loading, fetchData] = useGetGroupOrSelfPrescriptionList({
        current: 1,
        size: PAGE_SIZE,
        providerId,
        viewType,
    });
    const [isEdit, setIsEdit] = React.useState(false);

    const [page, setPage] = React.useState(1);
    const navigate = useNavigate();
    const [pendingNameFilter, setPendingNameFilter] = React.useState('');
    const [nameFilter, setNameFilter] = React.useState('');
    const [pendingDrugNameFilter, setPendingDrugNameFilter] = React.useState('');
    const [drugNameFilter, setDrugNameFilter] = React.useState('');

    const [statusFilters, setStatusFilters] = React.useState<EPrescriptionListStatus[]>([]);

    const [pageFilterValues, setPageFilterValues] = React.useState<number[]>([]);
    const [currentSelectItem, setCurrentSelectItem] = React.useState<TPrescriptionListItem>();
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [sortType, setSortType] = React.useState<'DESC' | 'ASC'>();
    const [sortBy, setSortBy] = React.useState<EPrescriptionListSortBy>();

    React.useEffect(() => {
        setPage(1);
    }, [nameFilter, pageFilterValues]);

    const onEdit = (record: ProviderPageInfo) => {};

    const onSuccess = () => {};

    const handleView = (record: TPrescriptionListItem) => {
        setCurrentSelectItem(record);
        setShowEditModal(true);
    };

    // 修复：支持 sorter 可能是对象或数组的类型，并确保类型兼容 antd TableProps 的定义
    const onTableChange: TableProps<TPrescriptionListItem>['onChange'] = (
        pagination,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TPrescriptionListItem> | SorterResult<TPrescriptionListItem>[],
        // extra 也可加上，保证类型一致，但如当前没用可以省略
    ) => {
        const pageInx = pagination?.current || 1;
        setPage(pageInx);

        if (sorter && !Array.isArray(sorter) && sorter.order) {
            const orderType = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            const orderKey = sorter.field === 'expires' ? 'EXPIRES' : (sorter.field as string) || 'EXPIRES';
            setSortType(orderType);
            setSortBy(orderKey as EPrescriptionListSortBy);
        }

        // Handle status filters
        if (filters.status && Array.isArray(filters.status)) {
            setStatusFilters(filters.status as EPrescriptionListStatus[]);
        } else {
            setStatusFilters([]);
        }
    };

    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchData({
            current: page,
            size: PAGE_SIZE,
            patientName: nameFilter,
            drugName: drugNameFilter,
            status: statusFilters,
            sortType,
            sortBy,
            providerId,
            viewType,
        });
    }, [nameFilter, drugNameFilter, statusFilters, page, sortType, sortBy]);

    const columns: ColumnsType<TPrescriptionListItem> = [
        {
            title: 'PATIENT',
            dataIndex: 'clientFirstName',
            key: 'clientFirstName',
            width: '120px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search patient name"
                        value={pendingNameFilter}
                        onChange={(e) => {
                            setPendingNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setPendingNameFilter(pendingNameFilter);
                            setNameFilter(pendingNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPendingNameFilter(pendingNameFilter);
                                setNameFilter(pendingNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            render: (text: string, record: TPrescriptionListItem) => (
                <div className={s.patientCell}>
                    <div
                        onClick={() => {
                            if (showProviderColumn && record.identityId) {
                                window.open(
                                    `/dashboard/group-practice/prospects-detail/${record.identityId}`,
                                    '_blank',
                                );
                                return;
                            }
                            window.open(`/dashboard/prospects-detail/${record.bpClientId}`, '_blank');
                        }}
                        className={s.patientName}
                    >
                        {`${record.clientFirstName} ${record.clientLastName}`.trim()}
                    </div>
                </div>
            ),
        },
        ...(showProviderColumn
            ? [
                  {
                      title: 'PROVIDER',
                      dataIndex: 'providerFirstName',
                      key: 'provider',
                      width: '180px',
                      render: (_: string, record: TPracticePrescriptionItem) => {
                          const name = `${record.providerFirstName || ''} ${record.providerLastName || ''}`.trim();
                          if (!name) return <span>-</span>;
                          return (
                              <div className={s.providerCell}>
                                  <span className={s.providerName}>
                                      {name}{record.providerCredential ? `, ${record.providerCredential}` : ''}
                                  </span>
                              </div>
                          );
                      },
                  } as ColumnsType<TPrescriptionListItem>[number],
              ]
            : []),
        {
            title: 'PRESCRIPTION',
            dataIndex: 'drugName',
            key: 'drugName',
            width: '200px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search case ID"
                        value={pendingDrugNameFilter}
                        onChange={(e) => {
                            setPendingDrugNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setDrugNameFilter(pendingDrugNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setDrugNameFilter(pendingDrugNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingDrugNameFilter('');
                                setDrugNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            render: (drugName: string, record: TPrescriptionListItem) => {
                return (
                    <div>
                        <div className={s.drugName}>
                            <b>{drugName}</b>
                        </div>
                        <div className={s.drugLine1}>{record.drugLine1}</div>
                    </div>
                );
            },
        },
        {
            title: 'QUANTITY',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '120px',
            render: (quantity: string) => {
                return (
                    <div className={s.quantity}>
                        <b>{quantity}</b>
                    </div>
                );
            },
        },
        {
            title: 'EXPIRES',
            dataIndex: 'expires',
            key: 'expires',
            sortDirections: ['ascend', 'descend'],
            width: '120px',
            sorter: true,
            render: (expires: string) => {
                if (!expires) {
                    return '-';
                }
                const dayLeft = dayjs(expires).diff(dayjs(), 'day');
                let label = 'Expired';
                if (dayLeft > 0) {
                    label = `${dayLeft} days left`;
                }
                return (
                    <div>
                        <div>
                            <b>{dayjs(expires).format('MM/DD/YYYY')}</b>
                        </div>
                        <div>
                            <b>{label}</b>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            width: '120px',
            filters: [
                { text: EPrescriptionListStatus.ACTIVE, value: EPrescriptionListStatus.ACTIVE },
                { text: EPrescriptionListStatus.QUEUED, value: EPrescriptionListStatus.QUEUED },
            ],
            render: (status: string) => {
                if (status === EPrescriptionListStatus.ACTIVE) {
                    return (
                        <span className={s.statusActive}>
                            <b>{status}</b>
                        </span>
                    );
                }
                if (status === EPrescriptionListStatus.QUEUED) {
                    return (
                        <span className={s.statusQueued}>
                            <b>{status}</b>
                        </span>
                    );
                }
                return '-';
            },
        },
        {
            title: 'WRITTEN DATE',
            dataIndex: 'writtenDate',
            key: 'writtenDate',
            sortDirections: ['ascend', 'descend'],
            width: '150px',
            sorter: true,
            render: (writtenDate: string) => {
                if (!writtenDate) {
                    return '-';
                }
                return (
                    <div>
                        <div>
                            <b>{dayjs(writtenDate).format('MM/DD/YYYY')}</b>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'APPROVAL DATE',
            dataIndex: 'approveDate',
            key: 'approveDate',
            sortDirections: ['ascend', 'descend'],
            width: '150px',
            sorter: true,
            render: (approveDate?: string) => {
                if (!approveDate) {
                    return '-';
                }
                return (
                    <div>
                        <div>
                            <b>{approveDate ? dayjs(approveDate).format('MM/DD/YYYY') : '-'}</b>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'DESTINATION',
            dataIndex: 'drugName',
            key: 'drugName',
            width: '250px',
            sorter: true,
            render: (approveDate: string, record: TPrescriptionListItem) => {
                return (
                    <div>
                        <div>
                            <b>{record.drugName}</b>
                        </div>
                        <div>
                            <p>
                                {record.pharmacyBusinessName}, {record.pharmacyAddressLine1}
                            </p>
                            <p>
                                {record.pharmacyCity}, {record.pharmacyState}, {record.pharmacyPostalCode}
                            </p>
                            <p>Phone: {record.pharmacyPhone}</p>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'PRACTICE',
            dataIndex: 'drugName',
            key: 'drugName',
            width: '250px',
            sorter: true,
            render: (approveDate: string, record: TPrescriptionListItem) => {
                return (
                    <div>
                        <div>
                            <b>{record.practiceName}</b>
                        </div>
                        <div>
                            <p>
                                {record.practiceAddress1}
                                {record.practiceAddress2 ? `, ${record.practiceAddress2}` : ''}
                            </p>
                            <p>
                                {record.practiceCity}, {record.practiceState}, {record.practiceZipcode}
                            </p>
                            <p>Phone: {record.practicePhone}</p>
                        </div>
                    </div>
                );
            },
        },
        {
            title: '',
            width: '100px',
            fixed: 'right',
            render: (record: TPrescriptionListItem) => {
                return (
                    <div className={s.actionWrap}>
                        <span onClick={() => handleView(record)} className={s.link}>
                            View
                        </span>
                    </div>
                );
            },
        },
    ];
    return (
        <div className={s.wrap}>
            <div className={s.tableWrap}>
                <CommonTable
                    bordered
                    pagination={{
                        total,
                        current: page,
                        pageSize: PAGE_SIZE,
                        showSizeChanger: false,
                    }}
                    onChange={onTableChange}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    columns={columns}
                    data={data || []}
                />
            </div>
            <EditPrescribeMedicationModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setCurrentSelectItem(undefined);
                }}
                prospect={currentSelectItem}
            />
        </div>
    );
};

export default PrescriptionsPatientTable;
